import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  User, Building2, Briefcase, MapPin, Mail, Phone, Home, FileText, Clock, Calendar,
  ChevronLeft, Loader2, Cake, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import HorarioResumen from "@/components/HorarioResumen";
import VacacionesResumen from "@/components/VacacionesResumen";
import FotoTrabajadorEditor from "@/components/FotoTrabajadorEditor";
import TwoFactorSection from "@/components/TwoFactorSection";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 rounded-xl border border-zinc-100 bg-zinc-50/80 px-3 py-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" strokeWidth={1.5} />
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">{label}</p>
        <p className="text-sm font-medium text-zinc-800">{value}</p>
      </div>
    </div>
  );
}

export default function MiPerfil() {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardandoCumple, setGuardandoCumple] = useState(false);
  const [passForm, setPassForm] = useState({ actual: "", nueva: "", confirmar: "" });
  const [guardandoPass, setGuardandoPass] = useState(false);

  useEffect(() => {
    api.get("/trabajador/mi-perfil")
      .then(({ data: res }) => setData(res))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setCargando(false));
  }, []);

  const u = data?.usuario;

  if (cargando) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!u) {
    return <p className="py-12 text-center text-sm text-zinc-500">No se pudo cargar tu perfil.</p>;
  }

  const nombre = `${u.nombre} ${u.apellidos}`.trim();

  const onFotoUpdated = async (fotoUrl) => {
    setData((prev) => prev ? { ...prev, usuario: { ...prev.usuario, foto_url: fotoUrl } } : prev);
    await refreshUser();
  };

  const cambiarAvisoCompaneros = async (activo) => {
    setGuardandoCumple(true);
    try {
      await api.put("/trabajador/mi-perfil/cumpleanos", { cumpleanos_avisar_companeros: activo });
      setData((prev) => prev ? {
        ...prev,
        usuario: { ...prev.usuario, cumpleanos_avisar_companeros: activo },
      } : prev);
      toast.success(activo ? "Tus compañeros serán informados de tu cumpleaños" : "Preferencia actualizada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardandoCumple(false);
    }
  };

  const cambiarPassword = async (e) => {
    e.preventDefault();
    if (!passForm.actual) {
      toast.error("Indica tu contraseña actual");
      return;
    }
    if (!passForm.nueva || passForm.nueva.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (passForm.nueva !== passForm.confirmar) {
      toast.error("La confirmación no coincide");
      return;
    }
    if (passForm.nueva === passForm.actual) {
      toast.error("La nueva contraseña debe ser distinta de la actual");
      return;
    }
    setGuardandoPass(true);
    try {
      await api.put("/auth/password", { actual: passForm.actual, nueva: passForm.nueva });
      setPassForm({ actual: "", nueva: "", confirmar: "" });
      toast.success("Contraseña actualizada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGuardandoPass(false);
    }
  };

  return (
    <div className="space-y-5">
      <Link to="/fichar" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
        <ChevronLeft className="h-4 w-4" />
        Volver
      </Link>

      <PageHeader
        icon={User}
        title="Mi información"
        subtitle="Datos personales, organización y horario"
        gradient="from-indigo-600 via-violet-600 to-purple-600"
      />

      <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-white shadow-sm">
        <div className="bg-gradient-to-r from-indigo-50 to-violet-50 px-4 py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <FotoTrabajadorEditor
              fotoUrlProp={u.foto_url}
              uploadUrl="/trabajador/mi-foto"
              deleteUrl="/trabajador/mi-foto"
              onUpdated={onFotoUpdated}
              size="lg"
            />
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">{nombre}</h2>
              <p className="text-sm text-zinc-500">{u.email}</p>
              {u.activo === false && (
                <Badge variant="outline" className="mt-1 border-rose-200 text-rose-700">Inactivo</Badge>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Contacto</p>
          <InfoRow icon={Mail} label="Email" value={u.email} />
          <InfoRow icon={Phone} label="Teléfono" value={u.telefono} />
          <InfoRow icon={Home} label="Dirección" value={[u.direccion, u.poblacion, u.provincia].filter(Boolean).join(", ") || null} />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Organización</p>
        <div className="grid gap-2">
          <InfoRow icon={Building2} label="Centro de trabajo" value={u.centro} />
          <InfoRow icon={MapPin} label="Departamento" value={u.departamento} />
          <InfoRow icon={Briefcase} label="Puesto" value={u.puesto} />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Contrato y jornada</p>
        <div className="grid gap-2">
          <InfoRow icon={FileText} label="Tipo de contrato" value={u.tipo_contrato} />
          <InfoRow icon={Clock} label="Tipo de jornada" value={data.modo_jornada_label} />
          {u.fecha_inicio_contrato && (
            <InfoRow icon={Calendar} label="Inicio de contrato" value={fmtFecha(u.fecha_inicio_contrato)} />
          )}
          {u.fecha_nacimiento && (
            <InfoRow icon={Calendar} label="Fecha de nacimiento" value={fmtFecha(u.fecha_nacimiento)} />
          )}
        </div>
      </div>

      {data.cumpleanos_activo && u.fecha_nacimiento && (
        <div className="rounded-2xl border border-pink-100 bg-pink-50/40 p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-3">
              <Cake className="mt-0.5 h-5 w-5 shrink-0 text-pink-600" />
              <div>
                <p className="text-sm font-medium text-pink-950">Informar a compañeros del centro</p>
                <p className="mt-1 text-xs text-pink-800/70">
                  Si lo activas, el día de tu cumpleaños el resto de trabajadores de tu centro recibirán un aviso para felicitarte.
                </p>
              </div>
            </div>
            <Switch
              checked={!!u.cumpleanos_avisar_companeros}
              disabled={guardandoCumple}
              onCheckedChange={cambiarAvisoCompaneros}
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Seguridad</p>
        <form
          onSubmit={cambiarPassword}
          className="rounded-2xl border border-zinc-100 bg-white p-4 shadow-sm space-y-3"
          data-testid="cambiar-password-form"
        >
          <div className="flex items-start gap-3">
            <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-zinc-900">Cambiar contraseña</p>
              <p className="mt-0.5 text-xs text-zinc-500">
                Introduce tu contraseña actual y una nueva (mínimo 6 caracteres).
              </p>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="pass-actual">Contraseña actual</Label>
              <Input
                id="pass-actual"
                type="password"
                autoComplete="current-password"
                value={passForm.actual}
                onChange={(e) => setPassForm({ ...passForm, actual: e.target.value })}
                data-testid="pass-actual-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pass-nueva">Nueva contraseña</Label>
              <Input
                id="pass-nueva"
                type="password"
                autoComplete="new-password"
                value={passForm.nueva}
                onChange={(e) => setPassForm({ ...passForm, nueva: e.target.value })}
                data-testid="pass-nueva-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pass-confirmar">Confirmar nueva</Label>
              <Input
                id="pass-confirmar"
                type="password"
                autoComplete="new-password"
                value={passForm.confirmar}
                onChange={(e) => setPassForm({ ...passForm, confirmar: e.target.value })}
                data-testid="pass-confirmar-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={guardandoPass}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="pass-guardar-btn"
          >
            {guardandoPass ? "Guardando…" : "Actualizar contraseña"}
          </Button>
        </form>
        <TwoFactorSection />
      </div>

      {data.vacaciones && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">
            Vacaciones {data.vacaciones.anio}
          </p>
          <div className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm">
            <VacacionesResumen vacaciones={data.vacaciones} />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Horario laboral</p>
          <Badge variant="outline" className="text-[10px] border-indigo-200 text-indigo-700">
            {data.horario_origen_label}
          </Badge>
        </div>
        <HorarioResumen horario={data.horario_efectivo} />
      </div>
    </div>
  );
}
